import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { apiInstance } from "@repo/core/services/instances/api";
import { PROPOSTA_SITUACAO } from "@repo/core/services/entities/Proposals";

const proposalsResource = new ProposalsResource(apiInstance);

interface ProposalSituationResponse {
  strSituacao?: string;
  [key: string]: unknown;
}

interface UseProposalStatusPollingOptions {
  proposalId: string | number | null | undefined;
  enabled?: boolean;
  pollingInterval?: number;
  isReanalysis?: boolean;
  onStatusChange?: (status: string) => void;
  onApproved?: () => void;
  onRejected?: () => void;
  onDerived?: () => void;
}

interface UseProposalStatusPollingReturn {
  status: string | null;
  isLoading: boolean;
  isPolling: boolean;
  isApproved: boolean;
  isRejected: boolean;
  isDerived: boolean;
  error: Error | null;
}

const FINAL_STATUSES_ANALYSIS = [
  PROPOSTA_SITUACAO.PRE_PROPOSAL_APPROVED, // "16"
  PROPOSTA_SITUACAO.DATA_CONFERENCE, // "20"
  PROPOSTA_SITUACAO.PRE_PROPOSAL_REJECTED, // "17"
  PROPOSTA_SITUACAO.CANCELLED // "14"
];

const FINAL_STATUSES_REANALYSIS = [
  PROPOSTA_SITUACAO.APPROVED_AUTOMATICALLY, // "05"
  PROPOSTA_SITUACAO.REJECTED_AUTOMATICALLY, // "07"
  PROPOSTA_SITUACAO.MANUAL_ANALYSIS_QUEUE, // "12"
  PROPOSTA_SITUACAO.APPROVED_MANUALLY, // "06"
  PROPOSTA_SITUACAO.REJECTED_MANUALLY, // "08"
  PROPOSTA_SITUACAO.CANCELLED, // "14"
  PROPOSTA_SITUACAO.PRE_PROPOSAL_REJECTED // "17"
];

export const useProposalStatusPolling = ({
  proposalId,
  enabled = true,
  pollingInterval = 3000,
  isReanalysis = false,
  onStatusChange,
  onApproved,
  onRejected,
  onDerived
}: UseProposalStatusPollingOptions): UseProposalStatusPollingReturn => {
  const previousStatusRef = useRef<string | null>(null);
  const hasCalledCallbacksRef = useRef({
    approved: false,
    rejected: false,
    derived: false
  });
  const [hasFinalStatus, setHasFinalStatus] = useState(false);

  const { data, isLoading, error, isFetching } =
    useQuery<ProposalSituationResponse>({
      queryKey: ["proposalSituation", proposalId, isReanalysis],
      queryFn: async () => {
        if (!proposalId) {
          throw new Error("Proposal ID is required");
        }
        const response = await proposalsResource.getProposal(
          String(proposalId)
        );

        return response.data as unknown as ProposalSituationResponse;
      },
      enabled: enabled && !!proposalId && !hasFinalStatus,
      refetchInterval: (query) => {
        if (!query || !query.state) {
          return pollingInterval;
        }

        const queryState = query.state as
          | { data?: ProposalSituationResponse }
          | undefined;
        const queryData = queryState?.data;
        const status = queryData?.strSituacao;

        if (!status) {
          return pollingInterval;
        }

        if (isReanalysis) {
          // For reanalysis: stop polling if status is in final statuses list
          const isFinalStatus = FINAL_STATUSES_REANALYSIS.includes(
            status as PROPOSTA_SITUACAO
          );
          if (isFinalStatus) {
            return false;
          }
          return pollingInterval;
        } else {
          // For analysis: stop polling if status is in final statuses list
          const isFinalStatus = FINAL_STATUSES_ANALYSIS.includes(
            status as PROPOSTA_SITUACAO
          );
          if (isFinalStatus) {
            return false;
          }
          return pollingInterval;
        }
      },
      refetchIntervalInBackground: true
    });

  const currentStatus = data?.strSituacao || null;

  // Check if current status is final to disable polling completely
  const isFinalStatus = useMemo(() => {
    if (!currentStatus) return false;
    const finalStatuses = isReanalysis ? FINAL_STATUSES_REANALYSIS : FINAL_STATUSES_ANALYSIS;
    return finalStatuses.includes(currentStatus as PROPOSTA_SITUACAO);
  }, [currentStatus, isReanalysis]);

  // Update hasFinalStatus when we detect a final status
  useEffect(() => {
    if (isFinalStatus && currentStatus && !hasFinalStatus) {
      console.log(
        "Polling: Final status detected, stopping polling - status:",
        currentStatus
      );
      setHasFinalStatus(true);
    }
  }, [isFinalStatus, currentStatus, hasFinalStatus]);

  let isApproved: boolean;
  let isRejected: boolean;
  let isDerived: boolean;

  if (isReanalysis) {
    // For reanalysis: approved is 05 or 06, rejected is 07, 08, 14, or 17, derived is 12
    isApproved =
      currentStatus === PROPOSTA_SITUACAO.APPROVED_AUTOMATICALLY ||
      currentStatus === PROPOSTA_SITUACAO.APPROVED_MANUALLY;
    isRejected =
      currentStatus === PROPOSTA_SITUACAO.REJECTED_AUTOMATICALLY ||
      currentStatus === PROPOSTA_SITUACAO.REJECTED_MANUALLY ||
      currentStatus === PROPOSTA_SITUACAO.CANCELLED ||
      currentStatus === PROPOSTA_SITUACAO.PRE_PROPOSAL_REJECTED;
    isDerived = currentStatus === PROPOSTA_SITUACAO.MANUAL_ANALYSIS_QUEUE;
  } else {
    // For analysis: approved is 16, rejected is 17 or 14
    isApproved = currentStatus === PROPOSTA_SITUACAO.PRE_PROPOSAL_APPROVED;
    isRejected =
      currentStatus === PROPOSTA_SITUACAO.PRE_PROPOSAL_REJECTED ||
      currentStatus === PROPOSTA_SITUACAO.CANCELLED;
    isDerived = false;
  }

  const isPolling = isFetching && !isApproved && !isRejected && !isDerived;

  // Handle status changes
  useEffect(() => {
    if (currentStatus && currentStatus !== previousStatusRef.current) {
      previousStatusRef.current = currentStatus;

      // Always call onStatusChange when status changes
      onStatusChange?.(currentStatus);

      // Call callbacks only once when status changes to final status
      if (isApproved && !hasCalledCallbacksRef.current.approved) {
        hasCalledCallbacksRef.current.approved = true;
        onApproved?.();
      }

      if (isRejected && !hasCalledCallbacksRef.current.rejected) {
        hasCalledCallbacksRef.current.rejected = true;
        onRejected?.();
      }

      if (isDerived && !hasCalledCallbacksRef.current.derived) {
        hasCalledCallbacksRef.current.derived = true;
        onDerived?.();
      }
    }
  }, [
    currentStatus,
    isApproved,
    isRejected,
    isDerived,
    isReanalysis,
    onStatusChange,
    onApproved,
    onRejected,
    onDerived
  ]);

  // Reset callbacks ref when proposalId or isReanalysis changes
  useEffect(() => {
    console.log(
      "Polling: Reset callbacks - proposalId:",
      proposalId,
      "isReanalysis:",
      isReanalysis
    );
    hasCalledCallbacksRef.current = {
      approved: false,
      rejected: false,
      derived: false
    };
    previousStatusRef.current = null;
    setHasFinalStatus(false);
  }, [proposalId, isReanalysis]);

  // Log when polling starts/stops
  useEffect(() => {
    console.log(
      "Polling: enabled:",
      enabled,
      "proposalId:",
      proposalId,
      "isFetching:",
      isFetching,
      "currentStatus:",
      currentStatus
    );
  }, [enabled, proposalId, isFetching, currentStatus]);

  return {
    status: currentStatus,
    isLoading,
    isPolling,
    isApproved,
    isRejected,
    isDerived,
    error: error as Error | null
  };
};
